import { useEffect, useState } from 'react';

import { Box, Stack, Title, useMantineTheme } from '@mantine/core';
import { useScrollIntoView } from '@mantine/hooks';

import { useTranslation } from 'react-i18next';

import { Button } from '../../../../../../../../shared/components/UI/Button/Button';
import { SidepanelHeader } from '../../../../../../../../shared/components/UI/Sidepanel/Sidepanel';

import { AssistantThreadMessageRole } from '../../../../../../../../shared/entities/AssistantThreadMessage';
import type { ResponseProjectBlock } from '../../../../../../../../shared/entities/ResponseProjectBlock';
import { useUpdateResponseProjectBlockMutation } from '../../../../hooks/useUpdateResponseProjectBlock.hooks';
import { LoaderMessage } from './components/LoaderMessage';
import { MessageInput } from './components/MessageInput';
import ThreadMessage from './components/ThreadMessage';
import {
  useCreateResponseProjectThreadMessage,
  useGetResponseProjectThreadById,
} from './hooks/projectAssistant.hook';
import { useGenerateAnswer } from './hooks/useGenerateAnswer.hook';
import { getTextFromContentSegment } from './utils/getTextFromContentSegment';

interface ResponseProjectAssistantProps {
  block: ResponseProjectBlock;
  threadId: number;
  closePanel: () => void;
  onUpdateBlockContent: (blockId: number) => void;
}

type SubComponentProps = Pick<
  ResponseProjectAssistantProps,
  'block' | 'closePanel' | 'onUpdateBlockContent'
> & {
  threadId: number;
};

const PANEL_WIDTH = 803;

export function ResponseProjectAssistant(props: ResponseProjectAssistantProps) {
  const theme = useMantineTheme();

  return (
    <Stack
      w={PANEL_WIDTH}
      style={{
        flex: 1,
        overflow: 'hidden',
        background: theme.other.gradients.background2,
      }}
      spacing={0}
    >
      <Header {...props} />
      <Content {...props} />
    </Stack>
  );
}

type HeaderProps = Pick<ResponseProjectAssistantProps, 'block' | 'closePanel'>;

function Header({ block, closePanel }: HeaderProps) {
  const suffix = block.getBlockSuffix();
  const title = block.title;

  return (
    <SidepanelHeader
      header={
        <Title order={4}>
          {suffix}. &nbsp;{title}
        </Title>
      }
      closePanel={closePanel}
    />
  );
}

type ContentProps = SubComponentProps;

function Content(props: ContentProps) {
  return (
    <Stack h="100%" style={{ flex: 1, overflow: 'hidden' }}>
      <Stack pt="0" sx={{ flex: 1, overflow: 'hidden' }} spacing={0}>
        <Chat {...props} />
      </Stack>
    </Stack>
  );
}

type ChatProps = SubComponentProps;

function Chat({
  block,
  closePanel,
  onUpdateBlockContent,
  threadId,
}: ChatProps) {
  const {
    data: { messages = [], id },
  } = useGetResponseProjectThreadById({ blockId: block.id, threadId });

  const { t } = useTranslation('bidResponse');
  const [isCopied, setIsCopied] = useState(false);

  const { mutateAsync: updateBlock, isPending } =
    useUpdateResponseProjectBlockMutation(block.projectId);

  const { generateAnswer } = useGenerateAnswer();

  const lastMessage = messages.at(-1);
  const isLastMessageUser =
    lastMessage?.role === AssistantThreadMessageRole.USER;

  const { scrollIntoView, targetRef, scrollableRef } = useScrollIntoView<
    HTMLDivElement,
    HTMLDivElement
  >({ duration: 0 });

  useEffect(() => {
    scrollIntoView({ alignment: 'end' });
  }, [scrollIntoView, messages]);

  useEffect(() => {
    if (isLastMessageUser) {
      generateAnswer({ blockId: block.id, threadId: id });
    }
  }, [isLastMessageUser, generateAnswer, id, block.id]);

  const getTextFromLastMessage = () => {
    if (lastMessage) {
      return getTextFromContentSegment(lastMessage);
    }
    return { formatted: '', plainText: '' };
  };

  const copyTextFromLastMessage = () => {
    if (isCopied) {
      return;
    }

    const { plainText } = getTextFromLastMessage();
    navigator.clipboard.writeText(plainText).then(() => {
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 1000);
    });
  };

  const onValidate = async () => {
    if (!lastMessage) {
      return;
    }

    const { markdown } = getTextFromContentSegment(lastMessage);
    await updateBlock({
      blockId: block.id,
      payload: {
        content: markdown,
      },
    });

    onUpdateBlockContent(block.id);
    closePanel();
  };

  return (
    <>
      <Box ref={scrollableRef} pt="06" sx={{ flex: 1, overflowY: 'auto' }}>
        <Stack spacing="04" px="05" ref={scrollableRef}>
          {messages.map(message => (
            <ThreadMessage key={message.id} message={message} />
          ))}

          {isLastMessageUser && <LoaderMessage />}

          <div ref={targetRef} />
        </Stack>
      </Box>
      <Stack align="flex-end" spacing="02" px="05">
        <Button
          variant="light"
          color={isCopied ? 'teal' : 'primary'}
          onClick={copyTextFromLastMessage}
        >
          {isCopied
            ? t('projectDetail.assistant.copied')
            : t('projectDetail.assistant.copy')}
        </Button>
        <Button variant="light" onClick={onValidate} disabled={isPending}>
          {t('projectDetail.assistant.validate')}
        </Button>
      </Stack>
      <Box px="05" w="100%" pb="05">
        <ChatInput
          disabled={isLastMessageUser}
          threadId={id}
          blockId={block.id}
        />
      </Box>
    </>
  );
}

interface ChatInputProps {
  disabled: boolean;
  threadId: number;
  blockId: number;
}

function ChatInput({ disabled, threadId, blockId }: ChatInputProps) {
  const createThreadMessageMutation = useCreateResponseProjectThreadMessage({
    blockId,
    threadId,
  });

  const handleSubmit = (content: string) => {
    createThreadMessageMutation.mutate({ content });
  };

  const isInputDisabled = disabled || createThreadMessageMutation.isPending;

  return <MessageInput onSubmit={handleSubmit} isDisabled={isInputDisabled} />;
}
